import * as UC from '@uploadcare/file-uploader';
import { populateTemplate } from '../utils/utils.js';

UC.defineComponents(UC);

class UploadCareWrapper extends HTMLElement {
    constructor() {
        super();

        this.ctxProvider = document.querySelector('uc-upload-ctx-provider');

        if (!this.ctxProvider) {
            throw new Error('uc-upload-ctx-provider not found');
        }

        this.api = this.ctxProvider.getAPI();

        this.itemContainerEl = this.querySelector('[data-item-container]');
        if (!this.itemContainerEl) {
            throw new Error('Item container element [data-item-container] not found');
        }

        this.successEntries = [];

        this.addListeners();

        this.render();
    }

    addListeners() {
        this.ctxProvider.addEventListener('common-upload-success', this.handleUploadSuccess.bind(this));
        this.ctxProvider.addEventListener('file-removed', this.handleFileRemoved.bind(this));
        this.ctxProvider.addEventListener('file-url-changed', this.handleFileUrlChanged.bind(this));
        this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(e) {
        if (e.target.closest('[data-open-flow]')) {
            this.api.initFlow();
        }
    }

    handleUploadSuccess(e) {
        this.successEntries = e.detail.successEntries;
        this.render();
    }

    handleFileRemoved(e) {
        this.updateSuccessEntries();
        this.render();
    }

    handleFileUrlChanged(e) {
        this.updateSuccessEntries();
        this.render();
    }

    updateSuccessEntries() {
        // Get current state from the API
        this.disabled = false;

        try {
            if (this.api && this.api.getOutputCollectionState) {
                const outputCollectionState = this.api.getOutputCollectionState();
                if (outputCollectionState && Array.isArray(outputCollectionState.successEntries)) {
                    this.successEntries = outputCollectionState.successEntries;
                } else {
                    this.successEntries = [];
                }
            } else {
                // Fallback: assume empty if we can't get the collection
                this.successEntries = [];
            }
        } catch (error) {
            console.error('[UploadCareWrapper] Error updating success entries:', error);
            this.successEntries = [];
        }
    }

    render() {
        // Clear existing items to avoid duplicates
        this.itemContainerEl.innerHTML = '';
        let successEntries = [];

        if (!this.disabled) {
            successEntries = this.successEntries;
        }

        if (successEntries.length > 0) {
            [...this.querySelectorAll('[data-open-flow]')].forEach((button) => {
                button.textContent = 'Change image';
            });
        } else {
            [...this.querySelectorAll('[data-open-flow]')].forEach((button) => {
                button.textContent = 'Choose image';
            });
        }

        successEntries.forEach((entry, index) => {
            entry.index = index + 1;
            const item = new UploadCareItem({ entry });
            this.itemContainerEl.appendChild(item);
        });
    }
}

customElements.define('uploadcare-wrapper', UploadCareWrapper);

class UploadCareItem extends HTMLElement {
    // Cache the template to avoid repeated DOM queries
    static _templateCache = null;

    static get template() {
        if (!this._templateCache) {
            this._templateCache = document.querySelector('[data-uploaded-item-template]');

            if (!this._templateCache) {
                throw new Error('Template with selector [data-uploaded-item-template] not found');
            }

            if (!(this._templateCache instanceof HTMLTemplateElement)) {
                throw new Error(
                    'Element with selector [data-uploaded-item-template] is not a template element'
                );
            }
        }

        return this._templateCache;
    }

    constructor({ entry }) {
        super();

        if (!entry) {
            throw new Error('Entry parameter is required');
        }

        if (!entry.cdnUrl) {
            throw new Error('Entry must have a cdnUrl property');
        }

        if (typeof entry.index === 'undefined' || entry.index === null) {
            throw new Error('Entry must have an index property');
        }

        this.entry = entry;

        this.render();
    }

    render() {
        try {
            const uploaderDocumentFragment = populateTemplate(UploadCareItem.template, {
                cdn_url: this.entry.cdnUrl,
                name: this.entry.name || 'Uploaded file',
                index: this.entry.index
            });

            this.appendChild(uploaderDocumentFragment);
        } catch (error) {
            console.error('[UploadCareItem] Error rendering template:', error);
            throw new Error(`Failed to render upload item: ${error.message}`);
        }
    }
}

customElements.define('uploadcare-item', UploadCareItem);
